import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { useSetUserPinMutation } from "@/redux/services/user/user.api";
import { User } from "@/redux/services/user/user.type";
import { ModalProps } from "@/lib/type";
import { PIN_LENGTH, getPinErrorMessage, pinSchema } from "@/lib/pin";

type FormValues = { pin: string };

const SetUserPinModal: React.FC<ModalProps<User>> = ({
  isOpen,
  onClose,
  item,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(pinSchema),
  });

  const [setUserPin, { isLoading }] = useSetUserPinMutation();

  const onSubmit = async (data: FormValues) => {
    if (!item?.id) return;

    const res = await setUserPin({ id: item.id, pin: data.pin });

    if ("data" in res || !("error" in res)) {
      toast.success("Code PIN mis à jour");
      reset();
      onClose();
      return;
    }

    setError("pin", { message: getPinErrorMessage(res.error) });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader className="mb-6">
            <DialogTitle>Définir le code PIN</DialogTitle>
            {item?.fullName && (
              <p className="text-sm text-muted-foreground mt-1">
                Pour <strong>{item.fullName}</strong>
              </p>
            )}
          </DialogHeader>

          <div>
            <Input
              label="Code PIN"
              type="password"
              id="pin"
              aria-describedby="pin-hint"
              inputMode="numeric"
              maxLength={PIN_LENGTH}
              pattern="\d*"
              autoFocus
              autoComplete="new-password"
              {...register("pin")}
              error={errors?.pin}
            />
            <p id="pin-hint" className="text-xs text-muted-foreground mt-2">
              Le code PIN doit contenir exactement {PIN_LENGTH} chiffres.
            </p>
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" isLoading={isLoading}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SetUserPinModal;
